import React, { useState } from 'react';
import { Calculator as CalcIcon, TrendingUp, DollarSign, Calendar, PiggyBank, Target, Clock, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { trackCalculatorUse } from '../utils/analytics';
import SEO from './SEO';
import { pageSEO } from '../seoData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnhancedCalculator = () => {
  const [activeCalculator, setActiveCalculator] = useState('sip');
  const [formData, setFormData] = useState({
    monthlyInvestment: '',
    timePeriodYears: '',
    expectedAnnualReturn: '',
    currentAge: '',
    retirementAge: '',
    currentSavings: '',
    targetAmount: '',
    presentValue: '',
    monthlyExpenses: ''
  });
  
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const calculatorTypes = [
    {
      id: 'sip',
      name: 'SIP Calculator',
      icon: PiggyBank,
      description: 'Calculate returns on Systematic Investment Plans',
      color: 'blue'
    },
    {
      id: 'retirement',
      name: 'Retirement Planning',
      icon: Clock,
      description: 'Plan your retirement savings and corpus',
      color: 'green'
    },
    {
      id: 'future_value',
      name: 'Future Value',
      icon: TrendingUp,
      description: 'Calculate future value of your investments',
      color: 'purple'
    },
    {
      id: 'wealth',
      name: 'Wealth Planning',
      icon: Target,
      description: 'Plan to reach your wealth creation goals',
      color: 'orange'
    }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const calculateReturns = async () => {
    // Validation based on calculator type
    const validationRules = {
      sip: ['monthlyInvestment', 'timePeriodYears', 'expectedAnnualReturn'],
      retirement: ['monthlyInvestment', 'currentAge', 'retirementAge', 'expectedAnnualReturn'],
      future_value: ['presentValue', 'timePeriodYears', 'expectedAnnualReturn'],
      wealth: ['targetAmount', 'timePeriodYears', 'expectedAnnualReturn']
    };

    const requiredFields = validationRules[activeCalculator] || [];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsCalculating(true);
    
    try {
      // Calculate locally to avoid backend dependency for now
      let calculationResults = {};
      
      if (activeCalculator === 'sip') {
        calculationResults = calculateSIP();
      } else if (activeCalculator === 'retirement') {
        calculationResults = calculateRetirement();
      } else if (activeCalculator === 'future_value') {
        calculationResults = calculateFutureValue();
      } else if (activeCalculator === 'wealth') {
        calculationResults = calculateWealthPlanning();
      }
      
      setResults(calculationResults);
      toast.success('Calculation completed!');
      
      // Track calculator usage in Google Analytics
      trackCalculatorUse(activeCalculator, {
        calculator_name: calculationResults.calculator_type,
        investment_amount: calculationResults.total_investment || calculationResults.present_value || 0
      });
    } catch (error) {
      console.error('Error calculating returns:', error);
      toast.error('Failed to calculate returns');
    } finally {
      setIsCalculating(false);
    }
  };

  const calculateSIP = () => {
    const monthlyAmount = parseFloat(formData.monthlyInvestment);
    const years = parseInt(formData.timePeriodYears);
    const annualRate = parseFloat(formData.expectedAnnualReturn) / 100;
    const monthlyRate = annualRate / 12;
    const totalMonths = years * 12;

    const futureValue = monthlyAmount * (((1 + monthlyRate) ** totalMonths - 1) / monthlyRate);
    const totalInvestment = monthlyAmount * totalMonths;
    const totalReturns = futureValue - totalInvestment;

    return {
      calculator_type: 'SIP',
      total_investment: totalInvestment,
      maturity_value: futureValue,
      total_returns: totalReturns,
      monthly_withdrawal_at_8_percent: (futureValue * 0.08) / 12,
      monthly_withdrawal_at_10_percent: (futureValue * 0.10) / 12,
      monthly_withdrawal_at_12_percent: (futureValue * 0.12) / 12
    };
  };

  const calculateRetirement = () => {
    const monthlyAmount = parseFloat(formData.monthlyInvestment);
    const currentAge = parseInt(formData.currentAge);
    const retirementAge = parseInt(formData.retirementAge);
    const years = retirementAge - currentAge;
    const annualRate = parseFloat(formData.expectedAnnualReturn) / 100;
    const monthlyRate = annualRate / 12;
    const totalMonths = years * 12;

    const futureValue = monthlyAmount * (((1 + monthlyRate) ** totalMonths - 1) / monthlyRate);
    const totalInvestment = monthlyAmount * totalMonths;
    const monthlyExpenses = parseFloat(formData.monthlyExpenses) || 0;
    const requiredCorpus = monthlyExpenses * 12 * 25; // 25 years post retirement

    return {
      calculator_type: 'Retirement Planning',
      retirement_corpus: futureValue,
      total_investment: totalInvestment,
      years_to_retirement: years,
      monthly_pension_8_percent: (futureValue * 0.08) / 12,
      monthly_pension_10_percent: (futureValue * 0.10) / 12,
      corpus_adequacy: futureValue >= requiredCorpus ? 'Adequate' : 'Needs Increase',
      shortfall: Math.max(0, requiredCorpus - futureValue)
    };
  };

  const calculateFutureValue = () => {
    const presentValue = parseFloat(formData.presentValue);
    const years = parseInt(formData.timePeriodYears);
    const annualRate = parseFloat(formData.expectedAnnualReturn) / 100;

    const futureValue = presentValue * ((1 + annualRate) ** years);
    const totalReturns = futureValue - presentValue;

    return {
      calculator_type: 'Future Value',
      present_value: presentValue,
      future_value: futureValue,
      total_returns: totalReturns,
      annual_growth: (futureValue / presentValue - 1) / years * 100
    };
  };

  const calculateWealthPlanning = () => {
    const targetAmount = parseFloat(formData.targetAmount);
    const years = parseInt(formData.timePeriodYears);
    const annualRate = parseFloat(formData.expectedAnnualReturn) / 100;
    const monthlyRate = annualRate / 12;
    const totalMonths = years * 12;

    const requiredMonthlySIP = targetAmount * monthlyRate / (((1 + monthlyRate) ** totalMonths) - 1);
    const totalInvestment = requiredMonthlySIP * totalMonths;

    return {
      calculator_type: 'Wealth Planning',
      target_amount: targetAmount,
      required_monthly_sip: requiredMonthlySIP,
      total_investment: totalInvestment,
      time_period: years
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const renderForm = () => {
    const currentCalc = calculatorTypes.find(calc => calc.id === activeCalculator);
    
    return (
      <div className="space-y-6">
        {activeCalculator === 'sip' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Investment (₹)
              </label>
              <input
                type="number"
                name="monthlyInvestment"
                value={formData.monthlyInvestment}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="25,000"
                data-testid="monthly-investment-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Period (Years)</label>
              <input
                type="number"
                name="timePeriodYears"
                value={formData.timePeriodYears}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="10"
                min="1"
                max="50"
                data-testid="time-period-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expected Annual Returns (%)</label>
              <input
                type="number"
                name="expectedAnnualReturn"
                value={formData.expectedAnnualReturn}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="12"
                min="1"
                max="30"
                step="0.1"
                data-testid="expected-return-input"
              />
            </div>
          </>
        )}

        {activeCalculator === 'retirement' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Age</label>
              <input
                type="number"
                name="currentAge"
                value={formData.currentAge}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="30"
                min="18"
                max="60"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Retirement Age</label>
              <input
                type="number"
                name="retirementAge"
                value={formData.retirementAge}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="60"
                min="50"
                max="70"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Investment (₹)</label>
              <input
                type="number"
                name="monthlyInvestment"
                value={formData.monthlyInvestment}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="50,000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expected Monthly Expenses (₹)</label>
              <input
                type="number"
                name="monthlyExpenses"
                value={formData.monthlyExpenses}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="50,000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expected Annual Returns (%)</label>
              <input
                type="number"
                name="expectedAnnualReturn"
                value={formData.expectedAnnualReturn}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="12"
                min="1"
                max="30"
                step="0.1"
              />
            </div>
          </>
        )}

        {activeCalculator === 'future_value' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Present Value (₹)</label>
              <input
                type="number"
                name="presentValue"
                value={formData.presentValue}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="10,00,000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Period (Years)</label>
              <input
                type="number"
                name="timePeriodYears"
                value={formData.timePeriodYears}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="10"
                min="1"
                max="50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expected Annual Returns (%)</label>
              <input
                type="number"
                name="expectedAnnualReturn"
                value={formData.expectedAnnualReturn}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="12"
                min="1"
                max="30"
                step="0.1"
              />
            </div>
          </>
        )}

        {activeCalculator === 'wealth' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Amount (₹)</label>
              <input
                type="number"
                name="targetAmount"
                value={formData.targetAmount}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1,00,00,000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Period (Years)</label>
              <input
                type="number"
                name="timePeriodYears"
                value={formData.timePeriodYears}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="15"
                min="1"
                max="50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expected Annual Returns (%)</label>
              <input
                type="number"
                name="expectedAnnualReturn"
                value={formData.expectedAnnualReturn}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="12"
                min="1"
                max="30"
                step="0.1"
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderResults = () => {
    if (!results) {
      return (
        <div className="text-center py-12" data-testid="no-results">
          <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <CalcIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Yet</h3>
          <p className="text-gray-600">
            Enter your parameters and click calculate to see your results.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6" data-testid="calculation-results">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-lg text-white">
          <h3 className="text-lg font-bold mb-2">{results.calculator_type} Results</h3>
        </div>

        {activeCalculator === 'sip' && (
          <>
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Total Investment</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(results.total_investment)}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-700">Maturity Value</p>
                <p className="text-2xl font-bold text-green-800">
                  {formatCurrency(results.maturity_value)}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-700">Total Returns</p>
                <p className="text-xl font-bold text-blue-800">
                  {formatCurrency(results.total_returns)}
                </p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Monthly Withdrawal Scenarios (Corpus intact)</h4>
              <div className="space-y-2">
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>@ 8% Returns</span>
                  <span className="font-semibold">{formatCurrency(results.monthly_withdrawal_at_8_percent)}/month</span>
                </div>
                <div className="flex justify-between p-2 bg-blue-50 rounded">
                  <span>@ 10% Returns</span>
                  <span className="font-semibold">{formatCurrency(results.monthly_withdrawal_at_10_percent)}/month</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>@ 12% Returns</span>
                  <span className="font-semibold">{formatCurrency(results.monthly_withdrawal_at_12_percent)}/month</span>
                </div>
              </div>
            </div>
          </>
        )}

        {activeCalculator === 'retirement' && (
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700">Retirement Corpus</p>
              <p className="text-2xl font-bold text-green-800">
                {formatCurrency(results.retirement_corpus)}
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">Years to Retirement</p>
              <p className="text-xl font-bold text-blue-800">{results.years_to_retirement} years</p>
            </div>
            <div className={`p-4 rounded-lg ${results.corpus_adequacy === 'Adequate' ? 'bg-green-50' : 'bg-red-50'}`}>
              <p className={`text-sm ${results.corpus_adequacy === 'Adequate' ? 'text-green-700' : 'text-red-700'}`}>
                Corpus Adequacy
              </p>
              <p className={`text-lg font-bold ${results.corpus_adequacy === 'Adequate' ? 'text-green-800' : 'text-red-800'}`}>
                {results.corpus_adequacy}
              </p>
              {results.shortfall > 0 && (
                <p className="text-sm text-red-600 mt-1">
                  Shortfall: {formatCurrency(results.shortfall)}
                </p>
              )}
            </div>
          </div>
        )}

        {activeCalculator === 'future_value' && (
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Present Value</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(results.present_value)}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700">Future Value</p>
              <p className="text-2xl font-bold text-green-800">
                {formatCurrency(results.future_value)}
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">Total Returns</p>
              <p className="text-xl font-bold text-blue-800">
                {formatCurrency(results.total_returns)}
              </p>
            </div>
          </div>
        )}

        {activeCalculator === 'wealth' && (
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700">Target Amount</p>
              <p className="text-2xl font-bold text-green-800">
                {formatCurrency(results.target_amount)}
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">Required Monthly SIP</p>
              <p className="text-xl font-bold text-blue-800">
                {formatCurrency(results.required_monthly_sip)}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Investment</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(results.total_investment)}
              </p>
            </div>
          </div>
        )}

        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-lg text-white">
          <h4 className="font-bold mb-2">Ready to Start Your Journey?</h4>
          <p className="text-blue-100 text-sm mb-4">
            This is mathematics. Start your systematic investment journey with Rumtek Finvest today.
          </p>
          <div className="flex space-x-2">
            <button 
              onClick={() => window.location.href = '/contact'}
              className="bg-white text-blue-600 px-4 py-2 rounded font-medium hover:bg-gray-100 transition-colors"
            >
              Contact Our Advisor
            </button>
            <button 
              onClick={() => window.location.href = '/services'}
              className="border border-white text-white px-4 py-2 rounded font-medium hover:bg-white hover:text-blue-600 transition-colors"
            >
              Explore Services
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO {...pageSEO.calculator} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <CalcIcon className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="calculator-title">
            Financial Calculators
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Plan your financial future with our comprehensive calculators. 
            Calculate SIP returns, plan retirement, and track wealth creation goals.
          </p>
        </div>

        {/* Calculator Type Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {calculatorTypes.map((calc) => {
            const Icon = calc.icon;
            const isActive = activeCalculator === calc.id;
            
            return (
              <button
                key={calc.id}
                onClick={() => {
                  setActiveCalculator(calc.id);
                  setResults(null);
                }}
                className={`p-6 rounded-xl border-2 transition-all ${
                  isActive
                    ? `border-${calc.color}-500 bg-${calc.color}-50`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                data-testid={`calc-${calc.id}`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4 ${
                  isActive ? `bg-${calc.color}-100` : 'bg-gray-100'
                }`}>
                  <Icon className={`h-6 w-6 ${isActive ? `text-${calc.color}-600` : 'text-gray-600'}`} />
                </div>
                <h3 className={`font-bold mb-2 ${isActive ? `text-${calc.color}-900` : 'text-gray-900'}`}>
                  {calc.name}
                </h3>
                <p className="text-sm text-gray-600 text-center">{calc.description}</p>
              </button>
            );
          })}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-testid="calculator-form-title">
              {calculatorTypes.find(calc => calc.id === activeCalculator)?.name} Parameters
            </h2>
            
            {renderForm()}
            
            {/* Calculate Button */}
            <button
              onClick={calculateReturns}
              disabled={isCalculating}
              data-testid="calculate-btn"
              className="w-full mt-6 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center justify-center"
            >
              {isCalculating ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Calculating...
                </div>
              ) : (
                <>
                  <CalcIcon className="h-5 w-5 mr-2" />
                  Calculate
                </>
              )}
            </button>
          </div>
          
          {/* Results */}
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-testid="results-title">
              Results
            </h2>
            {renderResults()}
          </div>
        </div>
        
        {/* Educational Note */}
        <div className="mt-12 bg-yellow-50 border-l-4 border-yellow-400 p-6">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                <strong>Important Note:</strong> These calculations are for illustrative purposes only. 
                Actual returns may vary based on market conditions, fund performance, and other factors. 
                Past performance does not guarantee future results. Please consult with our investment advisors for personalized advice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedCalculator;